/** Contacts tab for the Customer Workspace (Demo Mode only). Manages client contacts, internal
 * owners, escalation paths, finance contacts, operational contacts, reporting contacts and
 * decision makers - see `../../../../mocks/data/customerManagementData.ts`. When rendered with
 * an `onChange` (the operational Customer Workspace - see `../index.tsx`), contacts can be
 * created, edited and deleted via a modal. When rendered without one (the onboarding detail
 * page - see `../../OnboardingDetail/index.tsx`), it stays a read-only list. */

import { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import { Add, EditOutlined, DeleteOutline, PersonRemoveOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import HubSpotIndicator from '../../../../components/shared/crm/HubSpotIndicator';
import {
  buildContactCrmDetails, CustomerContact, CustomerRecord, getClientCrmDetails,
} from '../../../../mocks/data/customerManagementData';

interface ContactsTabProps {
  customer: CustomerRecord;
  onChange?: (contacts: CustomerContact[]) => void;
}

const CONTACT_CATEGORIES: CustomerContact['category'][] = ['Client', 'Internal Owner', 'Escalation', 'Finance', 'Operational', 'Reporting', 'Decision Maker'];

const emptyContact: CustomerContact = {
  name: '', role: '', category: 'Client', site: '', email: '', phone: '',
};

const ContactsTab = ({ customer, onChange }: ContactsTabProps) => {
  const { t } = useTranslation('common');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formContact, setFormContact] = useState<CustomerContact>(emptyContact);
  const [hubspotContactIdInput, setHubspotContactIdInput] = useState('');
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const isEditable = Boolean(onChange);
  const portalId = getClientCrmDetails(customer).portalId;

  const openCreate = () => {
    setFormContact(emptyContact);
    setHubspotContactIdInput('');
    setEditingIndex(-1);
  };

  const openEdit = (index: number) => {
    setFormContact(customer.contacts[index]);
    setHubspotContactIdInput(customer.contacts[index].crm?.contactId ?? '');
    setEditingIndex(index);
  };

  const closeForm = () => {
    setEditingIndex(null);
    setFormContact(emptyContact);
    setHubspotContactIdInput('');
  };

  const handleFormSave = () => {
    if (!onChange || editingIndex === null) return;
    const contactToSave: CustomerContact = { ...formContact, crm: buildContactCrmDetails(hubspotContactIdInput, portalId) };
    if (editingIndex === -1) {
      onChange([...customer.contacts, contactToSave]);
    } else {
      onChange(customer.contacts.map((contact, i) => (i === editingIndex ? contactToSave : contact)));
    }
    closeForm();
  };

  const handleDeleteConfirm = () => {
    if (!onChange || deleteIndex === null) return;
    onChange(customer.contacts.filter((_, i) => i !== deleteIndex));
    setDeleteIndex(null);
  };

  return (
    <Section>
      <TableHeader>
        {t('contactsTableTitle', 'Contacts')}
        <Pill text={`${customer.contacts.length} ${t('contactsCountLabel', 'contacts')}`} />
        {isEditable && (
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="secondary"
              size="md"
              startIcon={<Add />}
              onClick={openCreate}>
              {t('contactsAddContact', 'Add Contact')}
            </Button>
          </Box>
        )}
      </TableHeader>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('contactsName', 'Name')}</TableCell>
              <TableCell>{t('contactsRole', 'Role')}</TableCell>
              <TableCell>{t('contactsCategory', 'Category')}</TableCell>
              <TableCell>{t('contactsSite', 'Location')}</TableCell>
              <TableCell>{t('contactsEmail', 'Email')}</TableCell>
              <TableCell>{t('contactsPhone', 'Phone')}</TableCell>
              {isEditable && <TableCell>{t('contactsActions', 'Actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {customer.contacts.map((contact, index) => (
              <TableRow key={contact.email || `${contact.name}-${index}`}>
                <TableCell>
                  <Stack direction="row"
                    spacing={0.5}
                    alignItems="center">
                    <span>{contact.name}</span>
                    {contact.crm?.contactId && (
                      <HubSpotIndicator
                        url={contact.crm.contactUrl ?? undefined}
                        ariaLabel={t('contactsOpenHubSpotRecord', 'Open linked HubSpot contact record')}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{contact.role}</TableCell>
                <TableCell>
                  <Chip label={contact.category}
                    size="small"
                    variant="outlined" />
                </TableCell>
                <TableCell>{contact.site}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.phone}</TableCell>
                {isEditable && (
                  <TableCell>
                    <IconButton size="small"
                      title={t('contactsEdit', 'Edit contact')}
                      onClick={() => openEdit(index)}>
                      <EditOutlined fontSize="small" />
                    </IconButton>
                    <IconButton size="small"
                      title={t('contactsDelete', 'Delete contact')}
                      onClick={() => setDeleteIndex(index)}>
                      <DeleteOutline fontSize="small"
                        color="error" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editingIndex !== null}
        onClose={closeForm}
        fullWidth
        maxWidth="sm">
        <DialogTitle>
          {editingIndex === -1 ? t('contactsAddContact', 'Add Contact') : t('contactsEditContact', 'Edit Contact')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 1 }}>
            <TextField
              label={t('contactsName', 'Name')}
              value={formContact.name}
              onChange={(e) => setFormContact((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label={t('contactsRole', 'Role')}
              value={formContact.role}
              onChange={(e) => setFormContact((prev) => ({ ...prev, role: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              select
              label={t('contactsCategory', 'Category')}
              value={formContact.category}
              onChange={(e) => setFormContact((prev) => ({ ...prev, category: e.target.value as CustomerContact['category'] }))}
              fullWidth
              size="small"
            >
              {CONTACT_CATEGORIES.map((category) => (
                <MenuItem key={category}
                  value={category}>{category}</MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('contactsSite', 'Location')}
              value={formContact.site}
              onChange={(e) => setFormContact((prev) => ({ ...prev, site: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label={t('contactsEmail', 'Email')}
              value={formContact.email}
              onChange={(e) => setFormContact((prev) => ({ ...prev, email: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label={t('contactsPhone', 'Phone')}
              value={formContact.phone}
              onChange={(e) => setFormContact((prev) => ({ ...prev, phone: e.target.value }))}
              fullWidth
              size="small"
            />
            <TextField
              label={t('contactsHubSpotId', 'HubSpot contact ID')}
              helperText={t('contactsHubSpotIdHelper', 'Optional. Links this contact to the corresponding contact record in HubSpot.')}
              value={hubspotContactIdInput}
              onChange={(e) => setHubspotContactIdInput(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="secondary"
            onClick={closeForm}>{t('cancel', 'Cancel')}</Button>
          <Button variant="primary"
            disabled={!formContact.name || !formContact.role}
            onClick={handleFormSave}>{t('save', 'Save')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        open={deleteIndex !== null}
        icon={<PersonRemoveOutlined />}
        variant="bad"
        title={t('contactsDeleteTitle', 'Delete contact')}
        message={t('contactsDeleteMessage', 'Are you sure you want to delete this contact? This cannot be undone.')}
        confirmButtonText={t('contactsDelete', 'Delete contact')}
        cancelButtonText={t('cancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteIndex(null)}
      />
    </Section>
  );
};

export default ContactsTab;
